import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Handshake, History, ShieldCheck, Scale, Gem, Wrench } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { IMAGES } from "@/lib/site";
import { useSEO } from "@/lib/seo";

const pillars = [
  {
    icon: History,
    title: "A Texas family business",
    text: "Family owned since 1991 — serving Texas communities from three showrooms, with the same faces and the same standards. When you buy from us, you're buying from people you'll see again.",
  },
  {
    icon: Handshake,
    title: "Real jewelry expertise",
    text: "Repairs, custom design, diamond setting and watch servicing done by our own team — your pieces never leave trusted hands.",
  },
  {
    icon: ShieldCheck,
    title: "Inspected with care",
    text: "Showroom and pre-owned pieces are checked by our team — watches authenticated, gold assayed, diamonds reviewed — before they're offered to you.",
  },
];

const commitments = [
  {
    icon: Scale,
    title: "Honest testing, in front of you",
    text: "When you sell to us, every test and every weigh-in happens at the counter where you can see it. No back rooms, no mystery numbers — the scale is yours to read.",
  },
  {
    icon: Gem,
    title: "Straight answers on quality",
    text: "10K or 18K, natural or lab-grown, solid or plated — we tell you exactly what a piece is and why it's priced the way it is. If we wouldn't sell it to family, we won't sell it to you.",
  },
  {
    icon: Wrench,
    title: "We stand behind the work",
    text: "Sizing, soldering, settings and servicing — and if something we did isn't right, bring it back and we make it right.",
  },
];

export default function OurPromisePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: "Our Promise | Gold Time Jewelry",
    canonicalPath: "/our-promise",
    description: "Family owned since 1991 — honest pricing and real expertise at Gold Time Jewelry's three Texas showrooms.",
  });

  return (
    <main data-testid="promise-page">
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-2xl">
            <Eyebrow>Our Promise</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="promise-page-heading">
              Trusted by the community <span className="text-[#C9A227]">since 1991</span>
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600">
              Whether you're choosing a watch you'll wear for decades, restoring a family piece,
              or designing something custom — you deserve straight answers and real craftsmanship.
              That's how a family business earns its name, and keeps it.
            </p>
          </Reveal>

          <div className="mt-16 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-8">
              {pillars.map(({ icon: Icon, title, text }, i) => (
                <Reveal key={title} delay={0.08 * (i + 1)}>
                  <div className="flex items-start gap-5" data-testid={`promise-page-pillar-${i}`}>
                    <Icon size={24} strokeWidth={1} className="text-[#C9A227] mt-1 shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-800">{title}</h3>
                      <p className="text-sm md:text-base text-neutral-600 mt-1 leading-relaxed max-w-md">{text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15} className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-6 items-center">
                <div className="overflow-hidden rounded-sm">
                  <img
                    src={IMAGES.precision}
                    alt="Watchmaker servicing a fine watch movement"
                    className="w-full h-[300px] lg:h-[380px] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
                <div className="overflow-hidden rounded-sm">
                  <img
                    src={IMAGES.craft}
                    alt="Jeweler setting a diamond ring at the workbench"
                    className="w-full h-[300px] lg:h-[380px] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900 mt-24" data-testid="promise-commitments-heading">
              What that means <span className="text-[#C9A227]">at the counter</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {commitments.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={0.06 * i}>
                <div className="bg-white border border-[#E8DCC4] rounded-sm p-7 h-full" data-testid={`promise-commitment-${i}`}>
                  <Icon size={24} strokeWidth={1.25} className="text-[#C9A227]" />
                  <h3 className="font-display text-lg text-neutral-900 mt-4">{title}</h3>
                  <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-20 bg-[#0D0D0D] rounded-sm p-8 lg:p-10 flex flex-wrap items-center justify-between gap-6" data-testid="promise-cta">
              <div>
                <h2 className="font-display text-2xl text-white">Come see the difference in person</h2>
                <p className="text-sm text-neutral-400 mt-2 max-w-lg">
                  Free, no-pressure valuations at all three showrooms — or browse the catalog and
                  pick your piece up at the counter.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/showrooms"
                  data-testid="promise-cta-showrooms"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-7 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300"
                >
                  Find a showroom <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/catalog"
                  data-testid="promise-cta-catalog"
                  className="inline-flex items-center gap-2 rounded-full border border-[#C9A227]/60 text-[#E8CC6E] text-sm font-semibold px-7 py-3 hover:bg-[#C9A227]/10 transition-colors duration-300"
                >
                  Browse the catalog
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
}
